import { usePermissions } from '@ltvco/refresh-lib/ctx';
import { vehiclesTypes } from '@ltvco/refresh-lib/v1';
import { useMutation } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';

import { ERROR_MESSAGES } from '../../constants';
import {
  extractVehicleDataFromReport,
  extractVehicleSearchParams,
  fetchVehicleReport,
  type VehicleValuationParams,
} from '../../utils';
import {
  buildRepairEstimateData,
  type RepairEstimateVehicleData,
} from '../utils';

interface UseRepairCostEstimatorReportProcessingParams {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  onProcessingComplete: () => void;
  onProcessingError: () => void;
  fetchedValuation?: vehiclesTypes.AutomobileValuation;
  setValuationParams: (params: VehicleValuationParams) => void;
  setError: (error: string) => void;
  location: string;
  repairDescription: string;
  shopQuote: string;
}

/**
 * Hook to process vehicle reports and generate repair cost estimate data
 */
export const useRepairCostEstimatorReportProcessing = ({
  onSendMessage,
  onProcessingComplete,
  onProcessingError,
  fetchedValuation,
  setValuationParams,
  setError,
  location,
  repairDescription,
  shopQuote,
}: UseRepairCostEstimatorReportProcessingParams) => {
  const { isFreePlan } = usePermissions();
  const [pendingData, setPendingData] = useState<{
    vehicleData: RepairEstimateVehicleData;
    userMileage?: number;
  } | null>(null);

  // When valuation comes back, finish the process
  useEffect(() => {
    if (!pendingData || !fetchedValuation) return;

    const { vehicleData, userMileage } = pendingData;

    if (fetchedValuation.clean_retail?.adjusted) {
      vehicleData.marketValue = fetchedValuation.clean_retail.adjusted;
      // Build repair estimate data with market value
      const message = buildRepairEstimateData(
        isFreePlan,
        vehicleData,
        location,
        repairDescription,
        shopQuote || null,
        userMileage,
      );
      onSendMessage?.(message);
      onProcessingComplete();
      setPendingData(null);
    }
    // If no market value, don't complete - wait for manual input
  }, [
    fetchedValuation,
    pendingData,
    onSendMessage,
    onProcessingComplete,
    isFreePlan,
    location,
    repairDescription,
    shopQuote,
  ]);

  // Create mutation for vehicle report processing
  const processVehicleReport = useMutation({
    mutationFn: async ({
      vehicleArgs,
      userMileage,
    }: {
      vehicleArgs: Record<string, string>;
      userMileage?: number;
    }) => {
      // 1. Get vehicle report
      const vehicleSearchParams = extractVehicleSearchParams(vehicleArgs);
      const reportResult = await fetchVehicleReport(
        vehicleSearchParams,
        isFreePlan,
      );

      if (!reportResult.success || !reportResult.reportData) {
        return { error: ERROR_MESSAGES.TECHNICAL_DIFFICULTIES };
      }

      // 2. Extract vehicle data
      const vehicleData = extractVehicleDataFromReport(reportResult.reportData);
      if (!vehicleData) {
        return { error: ERROR_MESSAGES.NO_VEHICLE_FOUND };
      }

      // 3. Validate we have minimum vehicle data (YMM)
      if (!vehicleData.year || !vehicleData.make || !vehicleData.model) {
        return { error: ERROR_MESSAGES.INVALID_INPUT };
      }

      // 4. Check if we already have market value from report
      const currentVehicle =
        reportResult.reportData?.entities?.vehicles?.automobiles?.[0];
      const reportMarketValue =
        currentVehicle?.valuation?.clean_retail?.adjusted;

      if (reportMarketValue) {
        vehicleData.marketValue = reportMarketValue;
        const message = buildRepairEstimateData(
          isFreePlan,
          vehicleData,
          location,
          repairDescription,
          shopQuote || null,
          userMileage,
        );
        return { vehicleData, message, skipValuation: true };
      }

      // 5. Use user-provided mileage or vehicle mileage
      const mileage = userMileage || vehicleData.mileage || 0;

      // 6. No market value in report, try to get it from valuation API
      const region =
        currentVehicle?.valuation?.region?.name ||
        (typeof vehicleSearchParams.state === 'string'
          ? vehicleSearchParams.state
          : 'NY');

      const valuationParams = {
        year: vehicleData.year.toString(),
        make: vehicleData.make,
        model: vehicleData.model,
        mileage,
        region,
        ...(vehicleSearchParams.vin && { vin: vehicleSearchParams.vin }),
        ...(vehicleSearchParams.licensePlate && {
          plate: vehicleSearchParams.licensePlate,
        }),
        ...(vehicleSearchParams.state && {
          state: vehicleSearchParams.state,
        }),
      };

      return {
        vehicleData,
        userMileage,
        valuationParams,
        skipValuation: false,
      };
    },
    onSuccess: (result) => {
      if (result.error) {
        setError(result.error);
        onProcessingError();
      } else if (result.skipValuation) {
        onSendMessage?.(result.message);
        onProcessingComplete();
      } else if (result.valuationParams) {
        setPendingData({
          vehicleData: result.vehicleData,
          userMileage: result.userMileage,
        });
        setValuationParams(result.valuationParams);
      }
    },
    // NO onError handler - let QueryWrapper's globalErrorHandler handle 426 errors
  });

  return { processVehicleReport, pendingData };
};
